import React, { useMemo, useState } from 'react';
import { BsChatLeftText, BsChevronDown, BsExclamationTriangle } from 'react-icons/bs';
import { ROUTE_AUTH_STATUS, formatMoney, isRouteCounted, summarizeBySalesperson, summarizeCustomerGoals } from '../../services/salesRoutesService';
import { ReviewActions, ReviewBadge, RouteCustomersToggle, RouteReviewMeta, customerCount } from './RouteParts';
import styles from '../../css/SalesRoutes.module.css';

/**
 * Resumen por vendedor: total esperado, rutas asignadas y clientes. Cada card es un acordeón
 * que despliega las rutas del vendedor; se pueden tener varias abiertas a la vez.
 * Va en su propio componente para poder moverlo a la pantalla de metas cuando exista.
 */

// Las que cuentan primero, luego por nombre. En revisión, las pendientes van antes que todas.
const routeRank = (route, reviewMode) => {
    if (reviewMode && route.authStatus === ROUTE_AUTH_STATUS.pending) return 0;
    return isRouteCounted(route) ? 1 : 2;
};

const notCountedNote = (row) => {
    const parts = [
        row.pendingRoutes > 0 && `+${row.pendingRoutes} pending`,
        row.rejectedRoutes > 0 && `+${row.rejectedRoutes} rejected`,
        row.inactiveRoutes > 0 && `+${row.inactiveRoutes} inactive`,
    ].filter(Boolean);
    return parts.length > 0 ? `${parts.join(', ')}, not counted` : '';
};

export default function SalespersonSummary({ routes, goals, salespersonName, reviewMode, reviewBusy, onAuthorize, onReject, onViewComment }) {
    const [expanded, setExpanded] = useState(() => new Set());

    const rows = useMemo(() => summarizeBySalesperson(routes, goals), [routes, goals]);

    const routesBySalesperson = useMemo(() => {
        const grouped = new Map();
        routes.forEach((r) => {
            if (!grouped.has(r.salespersonNo)) grouped.set(r.salespersonNo, []);
            grouped.get(r.salespersonNo).push(r);
        });
        grouped.forEach((list) => list.sort((a, b) => (
            routeRank(a, reviewMode) - routeRank(b, reviewMode)
            || a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' })
        )));
        return grouped;
    }, [routes, reviewMode]);

    const toggle = (salespersonNo) => {
        setExpanded((current) => {
            const next = new Set(current);
            if (next.has(salespersonNo)) next.delete(salespersonNo);
            else next.add(salespersonNo);
            return next;
        });
    };

    if (rows.length === 0) {
        return <div className={styles.empty}><p>There are no routes to summarize yet.</p></div>;
    }

    return (
        <ul className={`${styles.routeGrid} ${styles.summaryGrid}`}>
            {rows.map((row, index) => {
                const isOpen = expanded.has(row.salespersonNo);
                const panelId = `sr-summary-routes-${index}`;
                const salespersonRoutes = routesBySalesperson.get(row.salespersonNo) || [];
                return (
                    <li key={row.salespersonNo} className={`${styles.routeCard} ${styles.summaryCard} ${isOpen ? styles.summaryCardOpen : ''}`}>
                        <h2 className={styles.summaryHeading}>
                            <button
                                type="button"
                                className={styles.summaryToggle}
                                aria-expanded={isOpen}
                                aria-controls={panelId}
                                onClick={() => toggle(row.salespersonNo)}
                            >
                                <span className={styles.routeName}>{salespersonName(row.salespersonNo)}</span>
                                <span className={`${styles.summaryExpected} ${row.withGoal > 0 ? '' : styles.summaryExpectedEmpty}`}>
                                    <span className={styles.kpiLabel}>Expected sales</span>
                                    {row.withGoal > 0 ? (
                                        <>
                                            <span className={styles.summaryAmount}>{formatMoney(row.expected)}</span>
                                            <span className={styles.summaryNote}>{row.withGoal} of {row.customers.toLocaleString()} customers with goal</span>
                                        </>
                                    ) : <span className={styles.summaryNote}>No goals</span>}
                                </span>
                                <span className={styles.summaryFacts}>
                                    <span className={styles.summaryFact}>
                                        <span className={styles.summaryFactLabel}>Authorized routes</span>
                                        <span className={styles.summaryFactValue}>{row.countedRoutes}</span>
                                        {notCountedNote(row) && <span className={styles.summaryFactNote}>{notCountedNote(row)}</span>}
                                    </span>
                                    <span className={styles.summaryFact}>
                                        <span className={styles.summaryFactLabel}>Customers</span>
                                        <span className={styles.summaryFactValue}>{row.customers.toLocaleString()}</span>
                                    </span>
                                </span>
                                <span className={styles.summaryHint}>
                                    {isOpen ? 'Hide routes' : `View routes (${salespersonRoutes.length})`}
                                    <span className={styles.summaryHintRight}>
                                        {reviewMode && row.pendingRoutes > 0 && <span className={styles.summaryPendingChip}>{row.pendingRoutes} pending</span>}
                                        <BsChevronDown className={`${styles.summaryChevron} ${isOpen ? styles.summaryChevronOpen : ''}`} aria-hidden="true" />
                                    </span>
                                </span>
                            </button>
                        </h2>
                        {row.repeatedCustomers > 0 && (
                            <p className={styles.summaryWarning}>
                                <BsExclamationTriangle className={styles.summaryWarningIcon} aria-hidden="true" />
                                {row.repeatedCustomers === 1 ? '1 customer is' : `${row.repeatedCustomers} customers are`} on more than one route; each counts once.
                            </p>
                        )}
                        <ul id={panelId} hidden={!isOpen} className={styles.summaryRoutes}>
                            {salespersonRoutes.map((route) => {
                                const routeGoals = summarizeCustomerGoals(route.customers, goals);
                                return (
                                    <li key={route.id} className={styles.summaryRouteBlock}>
                                        <div className={`${styles.summaryRouteStack} ${route.active ? '' : styles.summaryRouteInactive}`}>
                                            <div className={styles.summaryRouteRow}>
                                                <span className={styles.rowText}>
                                                    <span className={styles.rowName}>{route.name}</span>
                                                    <span className={styles.rowMeta}>{customerCount(route.customers.length)}</span>
                                                    <span className={styles.routeRowBadges}>
                                                        <ReviewBadge status={route.authStatus} />
                                                        {!route.active && <span className={`${styles.badge} ${styles.badgeInactive}`}>Inactive</span>}
                                                    </span>
                                                </span>
                                                {routeGoals.withGoal > 0
                                                    ? <span className={styles.summaryRouteAmount}>{formatMoney(routeGoals.expected)}</span>
                                                    : <span className={styles.summaryRouteNoGoal}>No goals</span>}
                                            </div>
                                            {reviewMode && <RouteReviewMeta route={route} />}
                                            {reviewMode && <RouteCustomersToggle route={route} goals={goals} />}
                                            {reviewMode && <ReviewActions route={route} busy={reviewBusy} onAuthorize={onAuthorize} onReject={onReject} />}
                                            {!reviewMode && route.authStatus === ROUTE_AUTH_STATUS.rejected && (
                                                <button type="button" className={`${styles.linkBtn} ${styles.linkBtnDanger}`} onClick={() => onViewComment(route)}>
                                                    <BsChatLeftText aria-hidden="true" /> View rejection comment
                                                </button>
                                            )}
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    </li>
                );
            })}
        </ul>
    );
}
